import { useEffect, useState } from 'react';
import axios from 'axios';
import { BadgeCheck, CircleSlash, Coins, RefreshCw, SlidersHorizontal } from 'lucide-react';
import ExcludedAccountsModal from './ExcludedAccountsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/super-admin`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtVES = (n) => `VES ${Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleString('es-VE', { dateStyle: 'short', timeStyle: 'short' }) : '—');

// Créditos que el banco confirmó pero que el automático no pudo atar a una orden
// (varias órdenes del mismo monto y el pagador no desempata). Se muestran los
// candidatos con la cédula y el teléfono para que el humano decida.
export default function UnmatchedCreditsQueue({ setToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [showAccounts, setShowAccounts] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/unmatched-credits?limit=50`, { headers: headers() });
      setData(res.data);
    } catch (e) { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const apply = async (item, orderCode) => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/unmatched-credits/apply`,
        { id_ope_se: item.id_ope_se, order_code: orderCode }, { headers: headers() });
      setToast?.({ type: 'success', message: `${fmtVES(item.amount)} acreditados a ${orderCode}` });
      setOpen(null); await load();
    } catch (e) {
      const msg = e.response?.data?.detail || 'No se pudo acreditar';
      setErr(msg);
      setToast?.({ type: 'error', message: msg });
    } finally { setBusy(false); }
  };

  const dismiss = async (item) => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/unmatched-credits/dismiss`,
        { id_ope_se: item.id_ope_se, note }, { headers: headers() });
      setToast?.({ type: 'success', message: 'Crédito descartado' });
      setOpen(null); setNote(''); await load();
    } catch (e) {
      const msg = e.response?.data?.detail || 'No se pudo descartar';
      setErr(msg);
      setToast?.({ type: 'error', message: msg });
    } finally { setBusy(false); }
  };

  if (loading && !data) {
    return (
      <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-5 animate-pulse"
        data-testid="unmatched-credits-loading">
        <div className="flex items-center gap-3">
          <RefreshCw className="w-5 h-5 text-amber-700 animate-spin" />
          <p className="text-sm font-medium text-amber-900">
            Revisando pagos del banco sin orden emparejada…
          </p>
        </div>
      </div>
    );
  }

  if (!data || data.total_count === 0) return null;

  return (
    <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="unmatched-credits-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Coins className="w-6 h-6 text-amber-700 mt-0.5 shrink-0" />
          <div>
            <h3 className="font-bold text-amber-900" data-testid="unmatched-credits-title">
              {data.total_count} pago(s) en el banco sin orden emparejada — {fmtVES(data.total_amount)}
            </h3>
            <p className="text-xs text-amber-800 mt-1 max-w-3xl">
              El banco confirmó el dinero pero hay varias órdenes del mismo monto y la cédula o el
              teléfono del pagador no alcanzan para decidir. Hasta aplicarlos, el ledger queda por
              debajo del saldo real del banco. Mostrando {data.shown}.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAccounts(true)} data-testid="unmatched-credits-accounts"
            className="text-xs px-3 py-1.5 border rounded-lg bg-white hover:bg-gray-50 flex items-center gap-1">
            <SlidersHorizontal className="w-3 h-3" /> Cuentas excluidas
          </button>
          <button onClick={load} data-testid="unmatched-credits-refresh"
            className="text-xs px-3 py-1.5 border rounded-lg bg-white hover:bg-gray-50 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Revisar
          </button>
        </div>
      </div>

      {showAccounts && (
        <ExcludedAccountsModal setToast={setToast}
          onClose={(saved) => { setShowAccounts(false); if (saved) load(); }} />
      )}

      <div className="mt-4 space-y-2">
        {data.items.map((it) => (
          <div key={it.id_ope_se} className="bg-white rounded-lg border p-3"
            data-testid={`unmatched-credit-${it.reference}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="text-xs min-w-0">
                <p className="font-bold text-sm text-gray-900">
                  {fmtVES(it.amount)}
                  <span className="ml-2 font-normal text-gray-500">{it.company_name}</span>
                </p>
                <p className="text-gray-600 font-mono text-[11px]">
                  ref {it.reference} · banco {it.bank} · {fmtDate(it.op_at)}
                </p>
                <p className="text-gray-600">
                  Pagador {it.payer_document || '—'} · tel {it.payer_phone || '—'}
                  {it.concepto ? ` · concepto "${it.concepto}"` : ''}
                </p>
                <p className={it.candidate_count ? 'text-amber-700 font-medium' : 'text-gray-400'}>
                  {it.candidate_count} orden(es) del mismo monto en ±3 h
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  disabled={!it.candidate_count}
                  onClick={() => { setOpen({ type: 'apply', item: it }); setErr(''); }}
                  data-testid={`unmatched-apply-${it.reference}`}
                  className="px-3 py-1.5 text-xs rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-30"
                >Elegir orden</button>
                <button
                  onClick={() => { setOpen({ type: 'dismiss', item: it }); setNote(''); setErr(''); }}
                  data-testid={`unmatched-dismiss-${it.reference}`}
                  className="px-3 py-1.5 text-xs rounded-lg border bg-white hover:bg-gray-50 flex items-center gap-1"
                ><CircleSlash className="w-3 h-3" /> No es de Caura</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="unmatched-modal">
            <h4 className="font-bold mb-1">
              {open.type === 'apply' ? 'Elegir la orden que pagó este crédito' : 'Descartar el crédito'}
            </h4>
            <p className="text-xs text-gray-500 mb-4">
              {fmtVES(open.item.amount)} · ref {open.item.reference} · pagador {open.item.payer_document} · tel {open.item.payer_phone}
            </p>
            {err && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-4"
                data-testid="unmatched-modal-error">{err}</p>
            )}
            {open.type === 'apply' ? (
              <div className="space-y-2">
                {open.item.candidates.map((c) => (
                  <div key={c.order_id} className="border rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs">
                      <p className="font-bold text-sm">
                        {c.order_code} · {fmtVES(c.amount)}
                        <span className="ml-2 font-normal text-gray-500">{c.status}</span>
                      </p>
                      <p className="text-gray-600">
                        {c.payer_name || '—'} · {c.payer_document || '—'} · {c.payer_phone || '—'}
                      </p>
                      <p className="text-gray-500">{fmtDate(c.created_at)}</p>
                      {(c.doc_matches || c.phone_matches) && (
                        <p className="text-emerald-700 font-medium flex items-center gap-1">
                          <BadgeCheck className="w-3.5 h-3.5" />
                          Coincide {c.doc_matches ? 'la cédula' : ''}{c.doc_matches && c.phone_matches ? ' y ' : ''}{c.phone_matches ? 'el teléfono' : ''}
                        </p>
                      )}
                    </div>
                    <button
                      disabled={busy}
                      onClick={() => apply(open.item, c.order_code)}
                      data-testid={`unmatched-pick-${c.order_code}`}
                      className="px-3 py-1.5 text-xs rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-40"
                    >Acreditar a esta</button>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                  data-testid="unmatched-note" placeholder="Motivo (ej. depósito de agencia)"
                  className="w-full px-3 py-2 border rounded-lg mb-4" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setOpen(null)} className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
                  <button onClick={() => dismiss(open.item)} disabled={busy}
                    data-testid="unmatched-dismiss-confirm"
                    className="px-4 py-2 text-sm bg-gray-800 text-white rounded-lg">Descartar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
